(() => {
  if (customElements.get('cm-route-preview')) return;
  const journeys = new Map();
  let quotaUntil = 0;
  class RoutePreview extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;
      this.initialized = true;
      const anchor = this.form.querySelector('#cm-shipdata-badge');
      if (anchor && !this.form.contains(this)) {
        this.moving = true;
        anchor.before(this);
        this.moving = false;
      }
      this.status = this.querySelector('[role="status"]');
      this.image = this.querySelector('[data-preview-image]');
      this.largeImage = this.querySelector('[data-large-image]');
      this.thumbnail = this.querySelector('[data-open-image]');
      this.loadingDialog = this.querySelector('[data-loading-dialog]');
      this.imageDialog = this.querySelector('[data-image-dialog]');
      this.stage = this.querySelector('[data-stage]');
      this.listeners = new AbortController();
      const options = {signal:this.listeners.signal};
      this.form.addEventListener('input', () => this.sync(), options);
      this.form.addEventListener('change', () => this.sync(), options);
      this.thumbnail.addEventListener('click', () => this.openDialog(this.imageDialog), options);
      this.querySelectorAll('[data-close-loading]').forEach(button => button.addEventListener('click', () => this.closeDialog(this.loadingDialog), options));
      this.querySelector('[data-close-image]').addEventListener('click', () => this.closeDialog(this.imageDialog), options);
      [this.loadingDialog,this.imageDialog].forEach(dialog => dialog.addEventListener('click', event => {
        if (event.target === dialog) this.closeDialog(dialog);
      }, options));
      this.timer = setInterval(() => this.sync(), 600);
      this.sync();
    }
    disconnectedCallback() {
      if (this.moving || !this.initialized) return;
      this.controller?.abort();
      this.listeners.abort();
      clearTimeout(this.debounce);
      clearInterval(this.timer);
      this.closeDialog(this.loadingDialog);
      this.closeDialog(this.imageDialog);
      this.clearImage();
      this.initialized = false;
      this.key = null;
    }
    openDialog(dialog) {
      if (this.isConnected && !dialog.open) dialog.showModal?.();
    }
    closeDialog(dialog) {
      if (dialog?.open) dialog.close();
    }
    values() {
      const value = name => this.form.querySelector(`[name="properties[${name}]"]`)?.value.trim() || '';
      return {ship:value('Name des Schiffs'), start:value('Startdatum'), end:value('Enddatum')};
    }
    reason({ship,start,end}) {
      const validDate = value => /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0,10) === value;
      if (!ship || !validDate(start) || !validDate(end) || end < start) return this.dataset.select;
      if ((Date.parse(end)-Date.parse(start))/86400000+1 > 21) return this.dataset.tooLong;
      if (end >= new Date().toISOString().slice(0,10)) return this.dataset.future;
      return '';
    }
    clearImage() {
      this.image?.removeAttribute('src');
      this.largeImage?.removeAttribute('src');
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
      if (this.thumbnail) this.thumbnail.hidden = true;
    }
    sync() {
      const values = this.values(), key = JSON.stringify(values);
      if (key === this.key) return;
      this.key = key;
      clearTimeout(this.debounce);
      this.controller?.abort();
      this.controller = null;
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.removeAttribute('data-review');
      this.closeDialog(this.loadingDialog);
      this.closeDialog(this.imageDialog);
      this.clearImage();
      const reason = this.reason(values);
      this.status.textContent = reason || '';
      if (!reason) this.debounce = setTimeout(() => this.generate(), 1200);
    }
    async request(path, options, signal) {
      const timeout = AbortSignal.timeout(30000);
      const response = await fetch(this.dataset.apiBase + '/storefront/previews' + path, {
        ...options, signal:signal ? AbortSignal.any([signal,timeout]) : timeout,
        credentials:'omit', cache:'no-store', redirect:'error'
      });
      if (!response.ok) {
        await response.body?.cancel().catch(() => {});
        if (response.status === 429) quotaUntil = Date.now() + Math.max(1,Math.min(86400,Number(response.headers.get('retry-after')) || 60))*1000;
        throw new Error(response.status === 429 ? this.dataset.limit : this.dataset.unavailable);
      }
      return response;
    }
    async pause(signal) {
      await new Promise((resolve, reject) => {
        const abort = () => {clearTimeout(timer); reject(new DOMException('Aborted','AbortError'));};
        const timer = setTimeout(() => {signal.removeEventListener('abort',abort); resolve();},3000);
        signal.addEventListener('abort',abort,{once:true});
        if (signal.aborted) abort();
      });
    }
    showNotice(result) {
      this.setAttribute('data-review','');
      this.status.textContent = result.message;
    }
    async display(result, signal) {
      this.clearImage();
      this.removeAttribute('data-review');
      this.objectUrl = URL.createObjectURL(result.blob);
      this.image.src = this.objectUrl;
      this.largeImage.src = this.objectUrl;
      if (this.image.decode) await this.image.decode();
      if (signal.aborted) return;
      this.thumbnail.hidden = false;
      this.status.textContent = result.message || this.dataset.ready;
    }
    async generate() {
      const values = this.values(), key = JSON.stringify(values);
      if (!this.isConnected || this.busy || key !== this.key || this.reason(values)) return;
      clearTimeout(this.debounce);
      const cached = journeys.get(key);
      if (!cached && Date.now() < quotaUntil) {
        this.setAttribute('data-review','');
        this.status.textContent = this.dataset.limit;
        return;
      }
      const controller = new AbortController(), signal = controller.signal;
      this.controller = controller;
      const timeout = setTimeout(() => controller.abort(new DOMException('Timeout','TimeoutError')),30*60*1000);
      this.busy = true;
      this.setAttribute('aria-busy','true');
      this.status.textContent = this.dataset.loading;
      this.stage.textContent = this.dataset.loading;
      if (!cached?.result) this.openDialog(this.loadingDialog);
      try {
        let entry = cached;
        if (!entry) {
          entry = {};
          journeys.set(key,entry);
          const customerId = this.dataset.customerId || '';
          entry.job = this.request('',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({...values,...(customerId?{customerId}:{})})})
            .then(response => response.json()).catch(error => {if (journeys.get(key) === entry) journeys.delete(key); throw error;});
        }
        if (entry.result) {
          if (entry.result.blob) await this.display(entry.result,signal);
          else this.showNotice(entry.result);
          return;
        }
        const job = await entry.job;
        if (signal.aborted) return;
        if (job.state === 'review') {
          entry.result = {message:job.message,review:true};
          this.showNotice(entry.result);
          return;
        }
        if (!/^[a-f0-9]{64}$/.test(job.id) || !job.accessToken) throw new Error(this.dataset.unavailable);
        const headers = {Authorization:'Bearer '+job.accessToken};
        this.stage.textContent = this.dataset.preparing;
        while (!signal.aborted) {
          const state = await (await this.request('/'+job.id,{headers},signal)).json();
          if (signal.aborted) return;
          if (state.state === 'error') {
            const review = state.message?.startsWith('Für diese Reise ist derzeit keine automatische Vorschau verfügbar.') === true;
            entry.result = {message:review ? state.message : this.dataset.unavailable,review};
            this.showNotice(entry.result);
            return;
          }
          if (state.state === 'ready') {
            this.stage.textContent = this.dataset.downloading;
            const imageResponse = await this.request('/'+job.id+'/image',{headers},signal);
            if (!imageResponse.headers.get('content-type')?.startsWith('image/png')) throw new Error(this.dataset.unavailable);
            const blob = await imageResponse.blob();
            if (signal.aborted) return;
            entry.result = {blob,message:state.message};
            await this.display(entry.result,signal);
            return;
          }
          await this.pause(signal);
        }
      } catch (error) {
        if (!signal.aborted || signal.reason?.name === 'TimeoutError') {
          if (this.controller !== controller) return;
          this.setAttribute('data-review','');
          this.status.textContent = signal.aborted ? this.dataset.timeout : error.message || this.dataset.unavailable;
        }
      } finally {
        clearTimeout(timeout);
        if (this.controller === controller) {
          this.busy = false;
          this.removeAttribute('aria-busy');
          this.closeDialog(this.loadingDialog);
        }
      }
    }
  }
  customElements.define('cm-route-preview', RoutePreview);
})();